<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Settings, RefreshCw, Eye, Calculator, Trash2, Server, X, Clock, CheckCircle, XCircle, Loader } from 'lucide-vue-next'
import { adminApi } from '@/services/api'
import { toast } from '@/utils/toast'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useI18n } from 'vue-i18n'
import type { Tenant } from '@/types'

const { t } = useI18n()
const router = useRouter()
const { authenticated, ensureAuthenticated } = useAdminAuth()

// Worker job type
interface WorkerJob {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled'
  totalRecords: number
  completedCount: number
  failedCount: number
  cancelledCount: number
  createdAt: number
  completedAt?: number
  generations: Array<{
    generationId: string
    status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed' | 'cancelled'
    currentStep?: string
    error?: string
    createdAt: number
  }>
}

// State
const tenants = ref<Tenant[]>([])
const updatingQuotas = ref(false)
const runningCleanup = ref(false)
const loadingWorkerStatus = ref(false)
const workerStatus = ref<{ success: boolean; workerUrl: string; activeJobCount?: number; jobs?: WorkerJob[]; error?: string } | null>(null)
const showJobsModal = ref(false)

// Load all tenants
const loadTenants = async () => {
  const key = ensureAuthenticated()
  if (!key) {
    router.push('/')
    return
  }

  try {
    tenants.value = await adminApi.listTenants(key)
  } catch (error: any) {
    console.error('Failed to load tenants:', error)
    toast.error('Failed to load tenants: ' + error.message)
    if (error.message.includes('403') || error.message.includes('401')) {
      authenticated.value = false
    }
  }
}

// Trigger quota update
const triggerQuotaUpdate = async () => {
  const key = ensureAuthenticated()
  if (!key) {
    router.push('/')
    return
  }

  try {
    updatingQuotas.value = true
    const result = await adminApi.triggerQuotaUpdate(key)
    toast.success(t('quotasUpdated') + ` (${result.results.length} tenants)`)
    // Reload tenants to show updated quotas
    await loadTenants()
  } catch (error: any) {
    console.error('Failed to update quotas:', error)
    toast.error(t('quotasUpdateFailed') + ': ' + error.message)
    if (error.message.includes('403') || error.message.includes('401')) {
      authenticated.value = false
    }
  } finally {
    updatingQuotas.value = false
  }
}

// Trigger cleanup
const triggerCleanup = async () => {
  const key = ensureAuthenticated()
  if (!key) {
    router.push('/')
    return
  }

  try {
    runningCleanup.value = true
    const result = await adminApi.triggerCleanup(key)
    const generations = result.results.generations
    const templates = result.results.templates
    toast.success(
      `Cleanup completed! Deleted ${generations.deletedCount} generations (${generations.r2DeletedCount} R2 files) and ${templates.deletedCount} templates (${templates.r2DeletedCount} R2 files)`
    )
  } catch (error: any) {
    console.error('Failed to run cleanup:', error)
    toast.error('Cleanup failed: ' + error.message)
    if (error.message.includes('403') || error.message.includes('401')) {
      authenticated.value = false
    }
  } finally {
    runningCleanup.value = false
  }
}

// Fetch worker status
const fetchWorkerStatus = async () => {
  const key = ensureAuthenticated()
  if (!key) {
    router.push('/')
    return
  }

  try {
    loadingWorkerStatus.value = true
    workerStatus.value = await adminApi.getWorkerStatus(key)
  } catch (error: any) {
    console.error('Failed to get worker status:', error)
    workerStatus.value = { success: false, workerUrl: '', error: error.message }
  } finally {
    loadingWorkerStatus.value = false
  }
}

// View tenant detail
const viewTenant = (tenantKey: string) => {
  router.push(`/admin/tenants/${tenantKey}`)
}

// Format timestamp
const formatDate = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A'
  return new Date(timestamp).toLocaleString()
}

// Format relative time
const formatRelativeTime = (timestamp: number): string => {
  const diff = Date.now() - timestamp
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m ago`
}

// Get status color class
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed': return 'text-green-600'
    case 'failed': return 'text-red-600'
    case 'cancelled': return 'text-gray-500'
    case 'processing':
    case 'uploading': return 'text-blue-600'
    default: return 'text-yellow-600'
  }
}

// On mount
onMounted(() => {
  loadTenants()
  fetchWorkerStatus()
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <!-- Header -->
    <div class="max-w-7xl mx-auto mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings class="w-8 h-8" />
            Tenant Administration
          </h1>
          <p class="text-gray-600 mt-2">Manage tenant plans and quotas</p>
        </div>
        <div class="flex items-center gap-3">
          <button
            @click="triggerCleanup"
            :disabled="!authenticated || runningCleanup"
            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Run cleanup to delete old templates and generations based on retention settings"
          >
            <Trash2 class="w-4 h-4" />
            {{ runningCleanup ? 'Cleaning...' : 'Run Cleanup' }}
          </button>
          <button
            @click="triggerQuotaUpdate"
            :disabled="!authenticated || updatingQuotas"
            class="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Calculator class="w-4 h-4" />
            {{ updatingQuotas ? 'Updating...' : t('updateQuotas') }}
          </button>
          <button
            @click="loadTenants"
            :disabled="!authenticated"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw class="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <!-- Worker Status Card -->
      <div class="mt-4 bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <Server class="w-5 h-5 text-gray-600" />
            <div>
              <h3 class="font-medium text-gray-900">Generation Worker</h3>
              <p v-if="workerStatus?.workerUrl" class="text-xs text-gray-500 font-mono">{{ workerStatus.workerUrl }}</p>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div v-if="loadingWorkerStatus" class="text-gray-500 text-sm">Loading...</div>
            <div v-else-if="workerStatus?.error" class="text-red-600 text-sm">{{ workerStatus.error }}</div>
            <div v-else-if="workerStatus?.success" class="flex items-center gap-4">
              <button
                @click="showJobsModal = true"
                class="text-center hover:bg-gray-100 rounded-lg p-2 -m-2 transition-colors"
                :disabled="!workerStatus.activeJobCount"
                :class="{ 'cursor-pointer': workerStatus.activeJobCount, 'cursor-default': !workerStatus.activeJobCount }"
              >
                <div class="text-2xl font-bold" :class="workerStatus.activeJobCount ? 'text-blue-600' : 'text-gray-400'">
                  {{ workerStatus.activeJobCount ?? 0 }}
                </div>
                <div class="text-xs text-gray-500">Active Jobs</div>
              </button>
            </div>
            <button
              @click="fetchWorkerStatus"
              :disabled="loadingWorkerStatus"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              title="Refresh worker status"
            >
              <RefreshCw :class="{ 'w-4 h-4': true, 'animate-spin': loadingWorkerStatus }" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tenants Table -->
    <div class="max-w-7xl mx-auto">
      <div class="bg-white rounded-lg shadow overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tenant Key
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monthly Quota
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Usage
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reset Day
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="!authenticated" class="text-center">
              <td colspan="8" class="px-6 py-12 text-gray-500">
                Please authenticate with admin API key
              </td>
            </tr>
            <tr v-else-if="tenants.length === 0" class="text-center">
              <td colspan="8" class="px-6 py-12 text-gray-500">
                No tenants found
              </td>
            </tr>
            <tr v-for="tenant in tenants" :key="tenant.lark_tenant_key">
              <!-- Tenant Key -->
              <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                {{ tenant.lark_tenant_key }}
              </td>

              <!-- Name -->
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ tenant.name || '-' }}
              </td>

              <!-- Plan -->
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span
                  :class="{
                    'px-2 py-1 text-xs rounded-full': true,
                    'bg-gray-100 text-gray-800': tenant.plan === 'free',
                    'bg-purple-100 text-purple-800': tenant.plan === 'pro',
                  }"
                >
                  {{ tenant.plan }}
                </span>
              </td>

              <!-- Monthly Quota -->
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="tenant.monthly_quota === null" class="text-gray-500 italic">
                  Unlimited
                </span>
                <span v-else class="text-gray-900 font-medium">
                  {{ tenant.monthly_quota }}
                </span>
              </td>

              <!-- Current Usage -->
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="tenant.monthly_quota === null" class="text-gray-600">
                  {{ tenant.current_quota || 0 }}
                </span>
                <span v-else-if="tenant.current_quota !== null"
                  :class="{
                    'font-medium': true,
                    'text-yellow-600': tenant.current_quota >= tenant.monthly_quota * 0.8,
                    'text-red-600': tenant.current_quota >= tenant.monthly_quota,
                    'text-gray-900': tenant.current_quota < tenant.monthly_quota * 0.8
                  }"
                >
                  {{ tenant.current_quota }} / {{ tenant.monthly_quota }}
                </span>
                <span v-else class="text-gray-500">
                  -
                </span>
              </td>

              <!-- Reset Day -->
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <span v-if="tenant.quota_reset_day_number === null" class="text-gray-500 italic">
                  N/A
                </span>
                <span v-else class="text-gray-900">
                  Day {{ tenant.quota_reset_day_number }}
                </span>
              </td>

              <!-- Created -->
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(tenant.created_at) }}
              </td>

              <!-- Actions -->
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  @click="viewTenant(tenant.lark_tenant_key)"
                  class="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                >
                  <Eye class="w-4 h-4" />
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Jobs Modal -->
    <div v-if="showJobsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Server class="w-5 h-5 text-gray-600" />
            <h2 class="text-lg font-semibold text-gray-900">Active Jobs</h2>
            <span class="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
              {{ workerStatus?.activeJobCount ?? 0 }}
            </span>
          </div>
          <button @click="showJobsModal = false" class="p-2 hover:bg-gray-100 rounded-lg">
            <X class="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <!-- Modal Body -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="!workerStatus?.jobs?.length" class="text-center text-gray-500 py-8">
            No active jobs
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="job in workerStatus.jobs"
              :key="job.jobId"
              class="border border-gray-200 rounded-lg p-4"
            >
              <!-- Job Header -->
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-3">
                  <span :class="['font-medium', getStatusColor(job.status)]">
                    {{ job.status.toUpperCase() }}
                  </span>
                  <span class="text-xs text-gray-500 font-mono">{{ job.jobId.slice(0, 8) }}...</span>
                </div>
                <div class="flex items-center gap-4 text-sm text-gray-500">
                  <span class="flex items-center gap-1">
                    <Clock class="w-3.5 h-3.5" />
                    {{ formatRelativeTime(job.createdAt) }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="flex items-center gap-1 text-green-600" v-if="job.completedCount">
                      <CheckCircle class="w-3.5 h-3.5" /> {{ job.completedCount }}
                    </span>
                    <span class="flex items-center gap-1 text-red-600" v-if="job.failedCount">
                      <XCircle class="w-3.5 h-3.5" /> {{ job.failedCount }}
                    </span>
                    <span class="flex items-center gap-1 text-blue-600" v-if="job.totalRecords - job.completedCount - job.failedCount - job.cancelledCount > 0">
                      <Loader class="w-3.5 h-3.5 animate-spin" /> {{ job.totalRecords - job.completedCount - job.failedCount - job.cancelledCount }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Generations List -->
              <div class="space-y-1">
                <div
                  v-for="gen in job.generations"
                  :key="gen.generationId"
                  class="flex items-center gap-3 text-sm py-1 px-2 rounded"
                  :class="{
                    'bg-green-50': gen.status === 'completed',
                    'bg-red-50': gen.status === 'failed',
                    'bg-blue-50': gen.status === 'processing' || gen.status === 'uploading',
                    'bg-gray-50': gen.status === 'pending' || gen.status === 'cancelled'
                  }"
                >
                  <span class="font-mono text-xs text-gray-400 w-20 truncate">{{ gen.generationId.slice(0, 8) }}</span>
                  <span :class="['text-xs font-medium w-20', getStatusColor(gen.status)]">
                    {{ gen.status }}
                  </span>
                  <span v-if="gen.currentStep" class="text-xs text-gray-600 flex-1 truncate">
                    {{ gen.currentStep }}
                  </span>
                  <span v-else-if="gen.error" class="text-xs text-red-600 flex-1 truncate" :title="gen.error">
                    {{ gen.error }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            @click="fetchWorkerStatus"
            :disabled="loadingWorkerStatus"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw :class="{ 'w-4 h-4': true, 'animate-spin': loadingWorkerStatus }" />
            Refresh
          </button>
          <button
            @click="showJobsModal = false"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Component-specific styles if needed */
</style>
